import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  CardHeader,
} from "reactstrap";
import ProfileSheetEditor, {
  SheetSection,
} from "Components/Common/ProfileSheet/ProfileSheetEditor";
import { updateContractorContactDetails } from "common/services/contractors.service";
import { tidyCountry } from "Components/Common/CountrySelect";
import { getContractor as onGetContractor } from "slices/thunks";
import Permission from "Components/Common/Permission";

/** Everything used to reach a contractor, edited together. */
const CONTACT_SECTIONS: SheetSection[] = [
  {
    kind: "list",
    id: "phoneNumber",
    label: "Phone numbers",
    icon: "ri-phone-line",
    noun: "phone number",
    plural: "phone numbers",
    placeholder: "For example +252 61 234 5678",
    inputType: "tel",
    hint: "Include the country code.",
    check: "phone",
  },
  {
    kind: "list",
    id: "emailAddress",
    label: "Email addresses",
    icon: "ri-mail-line",
    noun: "email address",
    plural: "email addresses",
    placeholder: "name@example.org",
    inputType: "email",
    check: "email",
  },
  {
    kind: "single",
    id: "website",
    label: "Website",
    icon: "ri-global-line",
    placeholder: "www.example.org",
    hint: "The contractor's own site, if it has one.",
    check: "web",
  },
  {
    kind: "list",
    id: "contactPersons",
    label: "Contact persons",
    icon: "ri-user-line",
    noun: "contact person",
    plural: "contact persons",
    placeholder: "Full name",
    hint: "People to contact at the contractor.",
  },
  {
    kind: "address",
    id: "physicalAddress",
    label: "Physical addresses",
    icon: "ri-map-pin-2-line",
    noun: "physical address",
    plural: "physical addresses",
    hint: "Where the contractor operates.",
  },
  {
    kind: "address",
    id: "postalAddress",
    label: "Postal addresses",
    icon: "ri-mail-send-line",
    noun: "postal address",
    plural: "postal addresses",
    hint: "Where post is received, if different.",
  },
];

const ContactDetails = ({ contractor }: { contractor: any }) => {
  const [openFlush, setOpenFlush] = useState("1");
  const toggleFlush = (id: any) => {
    if (openFlush !== id) {
      setOpenFlush(id);
    }
  };

  const dispatch: any = useDispatch();

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModal = () => {
    setIsUpdateModalOpen(true);
  };

  return (
    <Card>
      <CardHeader className="d-flex align-items-center">
        <div className="text-muted flex-grow-1 d-flex align-items-center">
          <h6 className="mb-0 fw-semibold text-uppercase">Contact Details</h6>
        </div>
        <div className="flex-shrink-0 d-flex align-items-center">
          <Permission
            resource={"contractor"}
            controller={"profile-details"}
            action={"updateContactDetails"}
          >
            <Link
              to="#"
              onClick={(e) => {
                e.stopPropagation(); // Prevent collapse toggle on edit click
                showUpdateModal();
              }}
              className="link-primary link-offset-2 text-decoration-underline link-underline-opacity-25 link-underline-opacity-100-hover me-3"
            >
              <i className="ri-pencil-line"></i> Edit
            </Link>
          </Permission>
        </div>
      </CardHeader>

      <CardBody className="card-body px-0 pt-0">
        <Row className="g-3">
          <Accordion
            id="default-accordion-example"
            flush
            open={openFlush}
            toggle={toggleFlush}
          >
            <AccordionItem className="material-shadow">
              <AccordionHeader targetId="1">Business Contacts</AccordionHeader>
              <AccordionBody accordionId="1">
                <Row>
                  <Col sm={6} className="">
                    <div className="d-flex mb-2 align-items-baseline">
                      <div className="flex-shrink-0">
                        <div className="avatar-xxs">
                          <div className="avatar-title bg-light text-danger rounded fs-17">
                            <i className="ri-phone-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className=" mb-1" id="billing-address-line-1">
                          <span className="fw-semibold text-body">Phone: </span>
                          <span>
                            {(contractor?.phoneNumber &&
                              contractor?.phoneNumber
                                .map((el: any) => el.name || "")
                                .join(", ")) ||
                              "Not Provided"}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="d-flex mb-2 align-items-baseline">
                      <div className="flex-shrink-0">
                        <div className="avatar-xxs">
                          <div className="avatar-title bg-light text-danger rounded fs-17">
                            <i className="ri-mail-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className=" mb-1" id="billing-address-line-1">
                          <span className="fw-semibold text-body">Email: </span>
                          <span>
                            {(contractor?.emailAddress &&
                              contractor.emailAddress
                                .map((email: any) => email.name || "")
                                .join(", ")) ||
                              "Not Provided"}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="d-flex mb-2 align-items-baseline">
                      <div className="flex-shrink-0">
                        <div className="avatar-xxs">
                          <div className="avatar-title bg-light text-danger rounded fs-17">
                            <i className="ri-map-pin-2-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className=" mb-1" id="billing-address-line-1">
                          <span className="fw-semibold text-body">
                            Physical Address:{" "}
                          </span>
                          <span>
                            {(contractor?.physicalAddress &&
                              contractor.physicalAddress
                                .map(
                                  (physical: any) =>
                                    [physical.address, physical.city, tidyCountry(physical.country)]
                                    .filter(Boolean)
                                    .join(", ")
                                )
                                .join(", ")) ||
                              "Not Provided"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6} className="">
                    <div className="d-flex mb-2 align-items-baseline">
                      <div className="flex-shrink-0">
                        <div className="avatar-xxs">
                          <div className="avatar-title bg-light text-danger rounded fs-17">
                            <i className="ri-map-pin-user-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className=" mb-1" id="billing-address-line-1">
                          <span className="fw-semibold text-body">
                            Postal Address:{" "}
                          </span>
                          <span>
                            {(contractor?.postalAddress &&
                              contractor.postalAddress
                                .map(
                                  (postal: any) =>
                                    [postal.address, postal.city, tidyCountry(postal.country)]
                                    .filter(Boolean)
                                    .join(", ")
                                )
                                .join(", ")) ||
                              "Not Provided"}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="d-flex mb-2 align-items-baseline">
                      <div className="flex-shrink-0">
                        <div className="avatar-xxs">
                          <div className="avatar-title bg-light text-danger rounded fs-17">
                            <i className="ri-global-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className=" mb-1" id="billing-address-line-1">
                          <span className="fw-semibold text-body me-1">
                            Business Website:
                          </span>
                          <span>{contractor?.website || "Not Provided"}</span>
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>
            {contractor?.contactPersons?.map((el: any, index: number) => {
              const actualId = `${index + 2}`;
              const contactPersonCount = index + 1;
              return (
                <AccordionItem className="material-shadow" key={index}>
                  <AccordionHeader targetId={actualId}>
                    Contact Person {contactPersonCount}
                  </AccordionHeader>
                  <AccordionBody accordionId={actualId}>
                    <Row>
                      <Col sm={6}>
                        <div className="d-flex">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm">
                              <div className="avatar-title bg-light text-danger rounded fs-24">
                                <i className="ri-user-line text-primary"></i>
                              </div>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <p className="mb-1">
                              <span className="fw-semibold text-body">
                                Name:
                              </span>{" "}
                              {el.name || "Not Provided"}
                            </p>

                            {/* <p className="text-muted mb-1">
                              <span className="fw-semibold text-body">
                                Phone:{" "}
                              </span>
                              <span id="shipping-phone-no">
                                {" "}
                                {el?.mobileNumber}
                              </span>
                            </p> */}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </AccordionBody>
                </AccordionItem>
              );
            })}
          </Accordion>
        </Row>
      </CardBody>
      <span onClick={(e) => e.stopPropagation()}>
        <ProfileSheetEditor
          isOpen={isUpdateModalOpen}
          toggle={() => setIsUpdateModalOpen(false)}
          title="Contact details"
          owner={contractor}
          sections={CONTACT_SECTIONS}
          save={(payload) => updateContractorContactDetails(contractor?.id, payload)}
          savedMessage="Contact details saved"
          refresh={() => dispatch(onGetContractor({ id: contractor?.id }))}
        />
      </span>
    </Card>
  );
};

export default ContactDetails;
